<template>
  <div>
    <BulkSendsOverviewSkeleton
      v-if="loading"
    />
    <div
      v-else
      :class="statusBorderCss"
      class="border border-solid border-l-{10px} border-{#D9D9D9} bg-whiteOverride rounded-md transition-all duration-200 cursor-default overflow-hidden mb-{25px} hover:shadow-appComponent hover:bg-envelopeOverviewHover">
      <div class="flex flex-col">
        <div class="flex flex-row p-{25px} lg:p-none">
          <div class="hidden lg:block pt-{25px} pb-{25px} pl-{25px}">
            <RouterLink
              :to="goTo"
              data-ec="bulk_send_overview_card"
              data-et="continue_draft_image_link"
            >
              <AtomImage
                class="border border-solid border-border rounded-md w-cardThumbnailW h-cardThumbnailH select-none"
                :src="bulkSend.thumbnail ? bulkSend.thumbnail : ''"
                :error="bulkSend.thumbnail ? require('@/assets/error.png') : undefined"
                :alt="$t('envelope.documentThumbnail')"
                data-hj-suppress
              />
            </RouterLink>
          </div>
          <div class="flex-grow lg:p-{25px}">
            <div class="flex flex-row lg:flex-col h-full">
              <div class="flex-grow flex flex-col pr-{25px} lg:pr-none">
                <RouterLink
                  :to="goTo"
                  class="group no-underline"
                  data-ec="bulk_send_overview_card"
                  data-et="continue_draft_title_link"
                >
                  <div class="text-20 text-black font-normal break-all group-hover:underline select-none">
                    {{ bulkSend.name }}
                  </div>
                  <div class="text-14 text-{#4B4B4B} mt-sm font-normal select-none">
                    {{ $t('envelope.created') }}
                    {{ bulkSend.created_at | formatDate('calendar') }}
                  </div>
                </RouterLink>
              </div>
              <div class="flex flex-col flex-wrap lg:flex-row lg:mt-{25px}">
                <ButtonLink
                  v-if="bulkSend.status !== 'draft'"
                  size="small"
                  class="card-button"
                  variant="tertiary"
                  :to="goTo"
                  data-ec="bulk_send_overview_card"
                  data-et="view_envelopes_button"
                >
                  <span>{{ $t('ui.bulkSend.overviewCards.viewAllEnvelopes') }}</span>
                </ButtonLink>
                <ButtonLink
                  v-if="bulkSend.status === 'draft'"
                  size="small"
                  class="card-button"
                  variant="tertiary"
                  :to="goTo"
                  data-ec="bulk_send_overview_card"
                  data-et="continue_draft_button"
                >
                  <span>{{ $t('ui.bulkSend.overviewCards.continueDraft') }}</span>
                </ButtonLink>
                <BaseButton
                  v-if="bulkSend.status === 'draft'"
                  size="small"
                  class="card-button"
                  variant="tertiary"
                  @click.native="$emit('delete', bulkSend.fingerprint, bulkSend.name)"
                  data-ec="bulk_send_overview_card"
                  data-et="delete_button">
                  <span>{{ $t('functional.delete') }}</span>
                </BaseButton>
              </div>
            </div>
          </div>
          <div class="hidden lg:flex flex-col w-{230px} 2xl:w-{300px} min-w-{230px} 2xl:min-w-{300px} p-{25px} border-1 border-border border-solid border-t-0 border-r-0 border-b-0 justify-between">
            <BulkSendsStatusPill
              :text="statusText"
              :variant="bulkSend.status"
            />
            <div class="flex flex-col justify-between">
              <div
                v-if="bulkSend.status === 'failed'"
                class="mt-sm font-normal text-black select-none">
                <span>{{ bulkSend.failed_message }}</span>
              </div>
              <BaseButton
                v-if="bulkSend.status === 'failed' && bulkSend.retries_attempted < 3"
                size="small"
                class="w-full mt-base"
                variant="tertiary"
                @click.native="retryBulkSend"
                data-ec="bulk_send_overview_card"
                data-et="retry_button">
                <span>{{ $t('functional.retry') }}</span>
              </BaseButton>
            </div>
          </div>
        </div>
        <div class="bg-offWhite py-base px-{25px} lg:hidden flex flex-col">
          <div class="flex justify-between w-full items-center">
            <BulkSendsStatusPill
              :text="statusText"
              :variant="bulkSend.status"
            />
            <div
              v-if="bulkSend.status === 'failed'"
              class="flex items-center group">
              <BaseButton
                class="group-hover:text-primaryGradient"
                @click.native="toggleMobileErrorPanel"
                variant="invisible"
                data-ec="bulk_send_overview_card"
                data-et="toggle_mobile_failure_button"
                size="small">
                {{ detailButtonText }}
              </BaseButton>
              <div
                @click="toggleMobileErrorPanel"
                class="group-hover:text-primaryGradient border border-placeholderGray border-solid p-xs rounded-full w-lg h-lg flex item-center justify-center group-hover:border-primaryGradient cursor-pointer">
                <ChevronUpIcon
                  class="cursor-pointer hover:text-primaryGradient group-hover:text-primaryGradient"
                  :class="{ 'rotate-180' : !showMobileErrorPanel }"
                />
              </div>
            </div>
          </div>
          <HeightTransition>
            <div v-if="bulkSend.status === 'failed' && showMobileErrorPanel">
              <div
                class="mt-sm font-normal text-black select-none">
                <span>{{ bulkSend.failed_message }}</span>
              </div>
              <BaseButton
                v-if="bulkSend.retries_attempted < 3"
                size="small"
                class="card-button mt-base"
                variant="tertiary"
                @click.native="retryBulkSend"
                data-ec="bulk_send_overview_card"
                data-et="mobile_retry_failed_bulk_send">
                <span>{{ $t('functional.retry') }}</span>
              </BaseButton>
            </div>
          </HeightTransition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BulkSendsStatusPill from '@/components/ui/bulkSend/BulkSendsStatusPill.vue';
import AtomImage from '@/components/atoms/Image.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import BaseButton from '@/components/library/buttons/BaseButton.vue';
import HeightTransition from '@/transitions/HeightTransition.vue';
import BulkSendsOverviewSkeleton from '@/components/ui/bulkSend/BulkSendsOverviewSkeleton.vue';
import useRequest from '@/composables/actions/request';
import { ChevronUpIcon } from '@/components/icons/fontIcons';

export default {
  name: 'BulkSendsOverviewCard.vue',
  components: {
    BulkSendsOverviewSkeleton,
    BulkSendsStatusPill,
    AtomImage,
    ButtonLink,
    BaseButton,
    HeightTransition,
    ChevronUpIcon,
  },
  props: {
    loading: {
      type: Boolean,
      required: true,
    },
    bulkSend: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const retryBulkSendRequest = useRequest('bulk-sends', { endpoint: 'retry' });

    return { retryBulkSendRequest };
  },
  data() {
    return {
      showMobileErrorPanel: false,
    };
  },
  methods: {
    async retryBulkSend() {
      await this.retryBulkSendRequest(this.bulkSend.fingerprint);
      this.$emit('refreshList');
    },
    toggleMobileErrorPanel() {
      this.showMobileErrorPanel = !this.showMobileErrorPanel;
    },
  },
  computed: {
    detailButtonText() {
      return this.showMobileErrorPanel ? this.$t('ui.bulkSend.overviewCards.hideDetail') : this.$t('ui.bulkSend.overviewCards.viewDetail');
    },
    statusText() {
      return this.$t(`bulkSend.status.${this.bulkSend.status}`);
    },
    statusBorderCss() {
      return {
        complete: 'border-l-bulkSendStatus-complete',
        draft: 'border-l-bulkSendStatus-draft',
        failed: 'border-l-bulkSendStatus-failed',
        processing: 'border-l-bulkSendStatus-processing',
      }[this.bulkSend.status];
    },
    goTo() {
      if (this.bulkSend.status === 'processing' || this.bulkSend.status === 'complete') {
        return {
          name: 'Envelopes',
          params: {
            pageNumber: 1,
          },
          query: {
            bulk_send_fingerprint: this.bulkSend.fingerprint,
            bulk_send_name: this.bulkSend.name,
          },
        };
      }

      return {
        name: 'BulkSendsSteps',
        params: {
          fingerprint: this.bulkSend.fingerprint,
        },
      };
    },
  },
};
</script>

<style scoped>
.card-button {
  @apply
  whitespace-nowrap
  mb-xs
  lg:mt-xs
  lg:mb-0
  lg:mr-xs
}
.card-button:last-child {
  @apply
  mb-0
  mr-0
}
</style>
